import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { currentLanguage } from '@/i18n'
import { formatDate, parseIsoDate, toIsoDate } from '@/lib/dates'

/** The Monday-based weekday of a date: 0 for Monday, 6 for Sunday. */
function weekday(date: Date): number {
  return (date.getDay() + 6) % 7
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function shiftMonth(month: Date, by: number): Date {
  return new Date(month.getFullYear(), month.getMonth() + by, 1)
}

/**
 * One month, Monday first.
 *
 * Monday rather than the language's own first day: the people using this work German trade fair
 * weeks, and a calendar that reshuffles itself when the interface is switched to English helps
 * nobody. Everything else — the month's name, the weekday headings — comes from `Intl`, exactly as
 * the rest of the date handling in here does.
 *
 * It holds no date of its own. [start] and [end] are what is marked, and a single date is simply
 * the two being the same day; deciding what a click means is the caller's job.
 */
export function MonthGrid({
  focusOn,
  start,
  end,
  caption,
  onSelect,
}: {
  /** The month to open on, as an ISO date. Empty opens on this one. */
  focusOn: string
  /** The first marked day, as an ISO date, or empty for none. */
  start: string
  /** The last marked day. Equal to [start] for a single date. */
  end: string
  /** What a click will do next, for a range being picked. */
  caption?: string
  onSelect: (value: string) => void
}) {
  const { t } = useTranslation()
  const language = currentLanguage()
  const today = new Date()
  const grid = useRef<HTMLDivElement>(null)
  const [month, setMonth] = useState(() => startOfMonth(parseIsoDate(focusOn) ?? today))

  const first = startOfMonth(month)
  const blanks = weekday(first)
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
  const todayIso = toIsoDate(today)

  const headings = Array.from({ length: 7 }, (_, index) =>
    // The first week of 2024 starts on a Monday, so this is simply seven consecutive days.
    new Intl.DateTimeFormat(language, { weekday: 'short' }).format(new Date(2024, 0, 1 + index)),
  )

  /**
   * Arrow keys walk the month a day or a week at a time. They stay inside it: stepping off the
   * edge into a month that is not on screen moves focus somewhere nobody can see.
   */
  function moveFocus(from: number, by: number) {
    const to = Math.min(Math.max(from + by, 1), days)
    grid.current?.querySelector<HTMLButtonElement>(`[data-day="${to}"]`)?.focus()
  }

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t('common.previousMonth')}
          onClick={() => setMonth(shiftMonth(month, -1))}
        >
          <ChevronLeftIcon />
        </Button>
        <span aria-live="polite" className="text-sm font-medium">
          {new Intl.DateTimeFormat(language, { month: 'long', year: 'numeric' }).format(month)}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t('common.nextMonth')}
          onClick={() => setMonth(shiftMonth(month, 1))}
        >
          <ChevronRightIcon />
        </Button>
      </div>

      <div className="text-muted-foreground grid grid-cols-7 gap-0.5 text-center text-xs">
        {headings.map((heading) => (
          <span key={heading} className="py-1">
            {heading}
          </span>
        ))}
      </div>

      <div ref={grid} className="grid grid-cols-7 gap-0.5">
        {Array.from({ length: blanks }, (_, index) => (
          <span key={`blank-${index}`} />
        ))}
        {Array.from({ length: days }, (_, index) => {
          const day = index + 1
          const iso = toIsoDate(new Date(month.getFullYear(), month.getMonth(), day))
          // ISO dates compare correctly as strings, which is the one thing yyyy-mm-dd is good at.
          const isEnd = iso === start || iso === end
          const inside = Boolean(start) && Boolean(end) && iso > start && iso < end
          return (
            <Button
              key={iso}
              type="button"
              data-day={day}
              variant={isEnd ? 'default' : 'ghost'}
              size="icon-sm"
              // The number alone says nothing out loud, and a screen reader reads the button, not
              // the column it sits under.
              aria-label={formatDate(iso)}
              aria-pressed={isEnd}
              aria-current={iso === todayIso ? 'date' : undefined}
              className={cellClass(inside, iso === todayIso && !isEnd)}
              onClick={() => onSelect(iso)}
              onKeyDown={(event) => {
                const by = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key]
                if (by === undefined) {
                  return
                }
                // Otherwise the arrow keys scroll the dialog behind the calendar as well.
                event.preventDefault()
                moveFocus(day, by)
              }}
            >
              {day}
            </Button>
          )
        })}
      </div>

      {caption && <p className="text-muted-foreground text-center text-xs">{caption}</p>}
    </div>
  )
}

function cellClass(inside: boolean, isToday: boolean): string | undefined {
  const marks = [inside && 'bg-accent text-accent-foreground', isToday && 'font-semibold ring-1 ring-ring/50']
  const className = marks.filter(Boolean).join(' ')
  return className || undefined
}
